/**
 * Product rules for org messaging / notifications (v1 direction).
 * **Shipped path:** org checklist nudge (Location Tools) → `ops_in_app_notifications` + Leads checklist banner; see `postChecklistBroadcastNudge` / `notifications-checklist-broadcast`.
 * Full narrative: `docs/v1.0-remaining-temp.md` → Notes and communication.
 *
 * Contact directory `visibility_level` is 1..3 (see `sql_schema.md`).
 * These labels map product language (“sales level”) to that scale for future APIs.
 */
export type ContactVisibilityLevel = 1 | 2 | 3;

/** Rough alias for docs that say “level 1 = sales users”. */
export const CONTACT_LEVEL_SALES: ContactVisibilityLevel = 1;

/**
 * Who may initiate a direct message to whom (baseline; confirm-only / read-receipt
 * rules will live on message rows when the backend exists).
 */
export function canInitiateDm(senderLevel: ContactVisibilityLevel, targetLevel: ContactVisibilityLevel): boolean {
  if (senderLevel >= 3) return true;
  if (senderLevel === 2) return true;
  // Level 1: only to peers (1) or level 2
  if (senderLevel === 1) return targetLevel === 1 || targetLevel === 2;
  return false;
}

/** Level-3 → level-1: product calls for outbound-only / confirm-only — enforce when messages ship. */
export function isOutboundOnlyForRecipient(senderLevel: ContactVisibilityLevel, recipientLevel: ContactVisibilityLevel): boolean {
  return senderLevel >= 3 && recipientLevel <= 1;
}

/**
 * Determines if sender may post a thread message to recipient.
 * This enforces outbound-only behavior for level-3 -> level-1 lanes by
 * preventing level-1 recipients from replying "up" to level-3 senders.
 */
export function canSendThreadMessage(
  senderLevel: ContactVisibilityLevel,
  recipientLevel: ContactVisibilityLevel
): boolean {
  if (!canInitiateDm(senderLevel, recipientLevel)) return false;
  if (senderLevel <= 1 && recipientLevel >= 3) return false;
  return true;
}

/**
 * Whether `viewer` may post a reply in an existing thread. A reply fans out to
 * every other participant, so the viewer must be allowed to message each of them.
 */
export function canReplyInThread(
  viewerUserId: string,
  viewerLevel: ContactVisibilityLevel,
  participantUserIds: readonly string[],
  trustLevelForUser: (userId: string) => ContactVisibilityLevel
): boolean {
  const others = participantUserIds.filter((id) => id && id !== viewerUserId);
  if (others.length === 0) return false;
  return others.every((uid) => canSendThreadMessage(viewerLevel, trustLevelForUser(uid)));
}

/** True when a higher-level sender opened an outbound-only lane for this viewer. */
export function isThreadOutboundOnlyForViewer(
  viewerUserId: string,
  viewerLevel: ContactVisibilityLevel,
  messages: ReadonlyArray<{ sender_user_id?: string | null }>,
  trustLevelForUser: (userId: string) => ContactVisibilityLevel
): boolean {
  for (const m of messages) {
    const sid = m.sender_user_id;
    if (!sid || sid === viewerUserId) continue;
    if (isOutboundOnlyForRecipient(trustLevelForUser(sid), viewerLevel)) return true;
  }
  return false;
}

/** Clamp arbitrary numeric trust levels (from API) into the 1..3 product scale. */
export function normalizeContactVisibilityLevel(raw: number | null | undefined): ContactVisibilityLevel {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 1;
  if (raw >= 3) return 3;
  if (raw <= 1) return 1;
  return 2;
}

/**
 * Build the dropdown list of org members the viewer is allowed to *start* a DM with.
 * Excludes the viewer themselves and anyone disallowed by `canInitiateDm`. Reply
 * permissions on existing threads are still enforced server-side via
 * `canSendThreadMessage`.
 */
export function filterMessageableMembers<M extends { user_id: string; trust_level: number }>(
  members: readonly M[],
  viewerUserId: string,
  viewerTrustLevel: ContactVisibilityLevel
): M[] {
  return members.filter((m) => {
    if (!m.user_id || m.user_id === viewerUserId) return false;
    return canInitiateDm(viewerTrustLevel, normalizeContactVisibilityLevel(m.trust_level));
  });
}

// ─── Archive gate (dev-sync 1e44bb90) ────────────────────────────────────────

export type ArchiveGateRow = {
  user_id: string;
  archived_at: string | null;
  requires_ack: boolean;
  acknowledged_at: string | null;
  sender_locked: boolean;
  sender_user_id: string | null;
  parent_notification_id: string | null;
};

export type ArchiveGateDecision =
  | { allowed: true }
  | { allowed: false; reason: "not-yours" | "already-archived" | "ack-required" | "sender-locked" | "sender-outranks" };

/**
 * Pure decision for "can `viewer` archive their copy of `row`?". The server
 * resolves `senderTrustLevel` + `senderHasArchivedTheirSeed` from the DB and
 * passes them in; the same helper runs client-side to disable the affordance
 * with a matching tooltip reason so the user never gets a surprise 403.
 *
 * Sender's own seed row (where viewer is also the sender) skips the gates
 * — they're allowed to archive their own outbox without acking themselves.
 */
export function canArchiveNotificationRow(input: {
  viewerUserId: string;
  viewerTrustLevel: ContactVisibilityLevel;
  row: ArchiveGateRow;
  senderTrustLevel: ContactVisibilityLevel | null;
  senderHasArchivedTheirSeed: boolean;
}): ArchiveGateDecision {
  const { viewerUserId, viewerTrustLevel, row, senderTrustLevel, senderHasArchivedTheirSeed } = input;
  if (row.user_id !== viewerUserId) return { allowed: false, reason: "not-yours" };
  if (row.archived_at) return { allowed: false, reason: "already-archived" };

  const isSenderSeed =
    !row.parent_notification_id &&
    row.sender_user_id != null &&
    row.sender_user_id === viewerUserId;
  if (isSenderSeed) return { allowed: true };

  if (row.requires_ack && !row.acknowledged_at) {
    return { allowed: false, reason: "ack-required" };
  }
  if (row.sender_locked && !senderHasArchivedTheirSeed) {
    return { allowed: false, reason: "sender-locked" };
  }
  if (
    row.sender_user_id &&
    senderTrustLevel != null &&
    senderTrustLevel > viewerTrustLevel &&
    !senderHasArchivedTheirSeed
  ) {
    return { allowed: false, reason: "sender-outranks" };
  }
  return { allowed: true };
}

/** Human-readable tooltip text for a blocked archive attempt. */
export function archiveBlockedReasonLabel(reason: Exclude<ArchiveGateDecision, { allowed: true }>["reason"]): string {
  switch (reason) {
    case "not-yours":
      return "You can only archive your own copy.";
    case "already-archived":
      return "Already archived.";
    case "ack-required":
      return "Acknowledge before archiving.";
    case "sender-locked":
      return "Sender locked this message until they clear it.";
    case "sender-outranks":
      return "Wait for the sender to clear their copy first.";
  }
}

/** Map server archive `skipped[].reason` codes to the same copy the UI tooltips use. */
export function archiveSkippedReasonsMessage(
  skipped: ReadonlyArray<{ reason?: string | null }> | undefined
): string | null {
  if (!skipped?.length) return null;
  const labels = [
    ...new Set(
      skipped.map((row) => {
        const reason = String(row.reason || "").trim();
        if (
          reason === "not-yours" ||
          reason === "already-archived" ||
          reason === "ack-required" ||
          reason === "sender-locked" ||
          reason === "sender-outranks"
        ) {
          return archiveBlockedReasonLabel(reason);
        }
        return reason || "Cannot archive yet.";
      })
    ),
  ];
  return labels.join(" ");
}

// ─── Follow-ups (from v1 Notes and communication — structured for future storage) ───

/** User-created reminder; must NOT block daily checklist progress; dismiss in one action. */
export type PersonalFollowUpScope = "lead_focused" | "user_only";

/**
 * Lead-focused: show on lead, checklist shell, and notifications. User-only: checklist + notifications only.
 * `followUpDate` is calendar day / instant the user chose (e.g. “call back Wednesday”).
 */
export type PersonalFollowUpDraft = {
  scope: PersonalFollowUpScope;
  /** Internal or GHL lead id when `lead_focused`. */
  leadRef?: string;
  followUpDate: string;
  note?: string;
};

/**
 * Org-wide lead hygiene: after N days without disposition / recontact, prompt assigned sales user.
 * Empty array = feature off. Acknowledging the prompt (e.g. confirming follow-up or disposition) satisfies the rule for that tier.
 */
export type LeadFollowupHygieneConfig = {
  promptAfterDays: number[];
};

export function normalizeHygieneDays(raw: readonly number[] | undefined): number[] {
  if (!raw?.length) return [];
  const out = [...new Set(raw.map((n) => Math.floor(n)).filter((n) => n >= 1 && n <= 365))];
  out.sort((a, b) => a - b);
  return out;
}

// ─── v1.x pre-v2 message lifecycle primitives ────────────────────────────────

export type WorkplaceMessageClass =
  | "org_nudge"
  | "manager_directive"
  | "compliance"
  | "lead_followup"
  | "personal_followup"
  | "task_assigned"
  | "task_update"
  | "general";

export type WorkplaceAckPolicy = "none" | "required";

export type WorkplaceReminderScope = "lead" | "user";
export type WorkplaceReminderStatus = "open" | "dismissed" | "resolved" | "reassigned";

/**
 * Defaults are intentionally conservative:
 * - hard directives and compliance notices require explicit ack
 * - informational nudges do not
 */
export function defaultAckPolicyForClass(kind: WorkplaceMessageClass): WorkplaceAckPolicy {
  if (kind === "manager_directive" || kind === "compliance") return "required";
  return "none";
}

export function canRequireAcknowledgement(
  senderLevel: ContactVisibilityLevel,
  recipientLevel: ContactVisibilityLevel,
  messageClass: WorkplaceMessageClass
): boolean {
  if (defaultAckPolicyForClass(messageClass) !== "required") return false;
  // Let level-2 and level-3 actors require ack for lower/equal recipients.
  if (senderLevel >= 2 && recipientLevel <= senderLevel) return true;
  return senderLevel >= 3;
}

export function normalizeReminderStatus(raw: string | null | undefined): WorkplaceReminderStatus {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "dismissed" || s === "resolved" || s === "reassigned") return s;
  return "open";
}

export function normalizeReminderScope(raw: string | null | undefined): WorkplaceReminderScope {
  return String(raw || "").trim().toLowerCase() === "lead" ? "lead" : "user";
}

/**
 * Parse cadence from org config input. Accepts either explicit array (e.g. [3,7,14])
 * or a single number (backward compatibility from earlier stale-day hint).
 */
export function normalizeHygieneCadence(raw: readonly number[] | number | null | undefined): number[] {
  if (Array.isArray(raw)) return normalizeHygieneDays(raw);
  if (typeof raw === "number" && Number.isFinite(raw)) return normalizeHygieneDays([raw]);
  return [];
}
